/**
 * Componente HeroSection
 * Hero inmersivo a pantalla completa con video/imagen de fondo
 * Estilo tipo Heinz con tipografía gigante y efecto parallax
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import AnimatedText from './AnimatedText';

gsap.registerPlugin(ScrollTrigger);

/**
 * Sección Hero inmersiva
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.title - Título principal
 * @param {string} props.subtitle - Subtítulo opcional
 * @param {string} props.backgroundVideo - URL del video de fondo
 * @param {string} props.backgroundImage - URL de la imagen de fondo (fallback)
 * @param {React.ReactNode} props.children - Contenido adicional (CTA, etc.)
 * @param {number} props.height - Altura del hero (en vh)
 * @param {boolean} props.overlay - Si debe mostrar overlay oscuro
 * @param {string} props.overlayGradient - Gradiente personalizado para el overlay
 * @param {string} props.textPosition - Posición del texto ('center', 'left', 'right', 'bottom')
 * @param {boolean} props.parallax - Si el fondo tiene efecto parallax
 * @param {string} props.className - Clases CSS adicionales
 * @returns {JSX.Element} Hero Section
 */
const HeroSection = ({
  title,
  subtitle,
  backgroundVideo,
  backgroundImage,
  children,
  height = 100,
  overlay = true,
  overlayGradient = 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)',
  textPosition = 'center',
  parallax = true,
  className = '',
}) => {
  const heroRef = useRef(null);
  const backgroundRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    const background = backgroundRef.current;
    const content = contentRef.current;

    if (!hero) return;

    // Efecto parallax en el fondo
    if (parallax && background) {
      gsap.to(background, {
        yPercent: 30,
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    }

    // Fade out del contenido al hacer scroll
    if (content) {
      gsap.to(content, {
        y: -100,
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'center center',
          end: 'bottom top',
          scrub: true,
        },
      });
    }

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === hero) {
          trigger.kill();
        }
      });
    };
  }, [parallax]);

  // Estilos de posición del texto
  const positionStyles = {
    center: 'items-center justify-center text-center',
    left: 'items-center justify-start text-left pl-8 md:pl-20',
    right: 'items-center justify-end text-right pr-8 md:pr-20',
    bottom: 'items-end justify-center text-center pb-20',
  };

  return (
    <section
      ref={heroRef}
      className={`relative overflow-hidden ${className}`}
      style={{ height: `${height}vh` }}
    >
      {/* Fondo (video o imagen) */}
      <div
        ref={backgroundRef}
        className="absolute inset-0 w-full h-[130%] -top-[15%]"
      >
        {backgroundVideo ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            poster={backgroundImage}
          >
            {/* WebM como fuente principal (mejor compresión) */}
            <source src={backgroundVideo.replace('.mp4', '.webm')} type="video/webm" />
            {/* MP4 como fallback para compatibilidad */}
            <source src={backgroundVideo} type="video/mp4" />
          </video>
        ) : backgroundImage ? (
          <img
            src={backgroundImage}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-intercasa-dark" />
        )}
      </div>

      {/* Overlay */}
      {overlay && (
        <div
          className="absolute inset-0 z-10"
          style={{ background: overlayGradient }}
        />
      )}

      {/* Contenido */}
      <div
        ref={contentRef}
        className={`relative z-20 h-full flex flex-col ${positionStyles[textPosition]} px-4 md:px-8`}
      >
        <div className="max-w-6xl w-full">
          {/* Título gigante animado */}
          {title && (
            <AnimatedText
              text={title}
              as="h1"
              animation="words"
              className="font-baskerville font-bold text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl leading-none mb-6 drop-shadow-2xl"
              staggerDelay={0.05}
            />
          )}

          {/* Subtítulo */}
          {subtitle && (
            <AnimatedText
              text={subtitle}
              as="p"
              animation="words"
              delay={0.3}
              className="font-lato text-white text-xl md:text-2xl lg:text-3xl opacity-90 max-w-3xl mx-auto mb-8 text-center"
            />
          )}

          {/* Contenido adicional (botones, etc.) */}
          {children && (
            <div className="mt-8 animate-fadeIn" style={{ animationDelay: '0.6s' }}>
              {children}
            </div>
          )}
        </div>

        {/* Indicador de scroll */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
};

HeroSection.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  backgroundVideo: PropTypes.string,
  backgroundImage: PropTypes.string,
  children: PropTypes.node,
  height: PropTypes.number,
  overlay: PropTypes.bool,
  overlayGradient: PropTypes.string,
  textPosition: PropTypes.oneOf(['center', 'left', 'right', 'bottom']),
  parallax: PropTypes.bool,
  className: PropTypes.string,
};

export default HeroSection;
